import {
  Button,
  ButtonGroup,
  Stack,
  Typography,
  FormControlLabel,
  Switch,
  useTheme,
  alpha,
} from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import SettingsPopout from '../../shared/SettingsPopout';
import {
  SynthOIPCImpactBarGroups,
  SynthOIChartLines,
  SynthOIPCImpactChartType,
  SynthOIPCImpactBarGroup,
  SynthOIPCImpactLine,
  SynthOIPCImpactTypeLabels,
  SynthOIPCLineLabels,
  SynthOIPCLineTooltip,
  SynthOIPCImpactBarGroupTooltip,
  getActiveLines,
  getActiveBarGroups,
} from 'types';
import InfoPopper from 'components/shared/InfoPopper';
import { useSynthOIPutCallImpactCtx } from './useSynthOIPutCallImpactCtx';

const SynthOIChartTypeSelector = () => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { config, updateConfig } = useSynthOIPutCallImpactCtx();
  const { chartType } = config;

  return (
    <Stack
      sx={{
        gap: 1,
        backgroundColor: alpha(theme.palette.gray, 0.1),
        borderRadius: 2,
        padding: '10px',
      }}
    >
      <Typography>Chart Type</Typography>
      <ButtonGroup aria-label="Chart type">
        {[
          SynthOIPCImpactChartType.Gamma,
          SynthOIPCImpactChartType.Delta,
          SynthOIPCImpactChartType.OpenInterest,
        ].map((candidate: SynthOIPCImpactChartType) => (
          <Button
            key={candidate}
            size="small"
            sx={{
              backgroundColor:
                chartType === candidate
                  ? alpha(theme.palette.secondary.main, 0.25)
                  : 'transparent',
              '&:hover': {
                color: alpha(theme.palette.secondary.main, 1),
                borderColor: alpha(theme.palette.secondary.main, 1),
              },
              fontSize: isMobile ? 12 : 14,
              textTransform: 'none',
              width: '33.3%',
            }}
            onClick={() => {
              updateConfig({ chartType: candidate });
            }}
          >
            {SynthOIPCImpactTypeLabels.get(candidate)!}
          </Button>
        ))}
      </ButtonGroup>
    </Stack>
  );
};

const SynthOIChartLineSelector = () => {
  const { config, updateConfig } = useSynthOIPutCallImpactCtx();
  const { chartType } = config;
  const selectedLines = getActiveLines(config);
  const lineTypes: SynthOIPCImpactLine[] = SynthOIChartLines.get(chartType)!;

  return lineTypes.length > 0 ? (
    <Stack>
      <Typography>Lines</Typography>
      {lineTypes.map((l) => (
        <Stack
          key={l}
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={selectedLines.includes(l)}
                onChange={() => {
                  const next = selectedLines.includes(l)
                    ? selectedLines.filter((x) => x !== l)
                    : [...selectedLines, l];
                  updateConfig({
                    linesByChartType: {
                      ...config.linesByChartType,
                      [chartType]: next,
                    },
                  });
                }}
              />
            }
            label={<Typography>{SynthOIPCLineLabels.get(l)}</Typography>}
            labelPlacement="end"
          />
          {SynthOIPCLineTooltip.get(l) != null && (
            <InfoPopper
              contents={[{ description: SynthOIPCLineTooltip.get(l)! }]}
            />
          )}
        </Stack>
      ))}
    </Stack>
  ) : null;
};

const SynthOIChartBarGroupSelector = () => {
  const { config, updateConfig } = useSynthOIPutCallImpactCtx();
  const { chartType } = config;
  const selectedBarGroups = getActiveBarGroups(config);
  const barGroups: SynthOIPCImpactBarGroup[] =
    SynthOIPCImpactBarGroups.get(chartType)!;

  return barGroups.length > 0 ? (
    <Stack>
      <Typography>Bars</Typography>
      {barGroups.map((barGroup) => (
        <Stack
          key={barGroup}
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={selectedBarGroups.includes(barGroup)}
                onChange={() => {
                  // Groups are mutually exclusive — only one active at a time
                  updateConfig({
                    barGroupsByChartType: {
                      ...config.barGroupsByChartType,
                      [chartType]: [barGroup],
                    },
                  });
                }}
              />
            }
            label={<Typography>{barGroup}</Typography>}
            labelPlacement="end"
          />
          {SynthOIPCImpactBarGroupTooltip.get(barGroup) != null && (
            <InfoPopper
              contents={[
                { description: SynthOIPCImpactBarGroupTooltip.get(barGroup)! },
              ]}
            />
          )}
        </Stack>
      ))}
    </Stack>
  ) : null;
};

const ShowKeyLevelsToggle = () => {
  const { config, updateConfig } = useSynthOIPutCallImpactCtx();
  return (
    <FormControlLabel
      control={
        <Switch
          checked={config.showKeyLevels}
          onChange={(_e, checked: boolean) =>
            updateConfig({ showKeyLevels: checked })
          }
        />
      }
      label={<Typography>Show key levels</Typography>}
      labelPlacement="end"
    />
  );
};

export const SynthOIPutCallImpactSettings = () => {
  const isMobile = useIsMobile();
  return (
    <SettingsPopout
      title="Put/Call Impact Settings"
      popperID="synth-oi-pc-impact-controls"
      placement="bottom-end"
      sx={{
        width: isMobile ? '280px' : '400px',
      }}
    >
      <Stack sx={{ gap: '8px' }}>
        <ShowKeyLevelsToggle />
        <SynthOIChartTypeSelector />
        <SynthOIChartLineSelector />
        <SynthOIChartBarGroupSelector />
      </Stack>
    </SettingsPopout>
  );
};
